import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import moment from 'moment';

import { makeFormField, stripNonNumericsKeepPeriod, getCountryCodeByTopLevelDomain } from '@/helpers';
import { ErrorMode, CmsImageUrls } from '@/models';

@Component({
  name: 'CmgRetroCreditOrderDetails',
})
export default class CmgRetroCreditOrderDetails extends Vue {
  purchaseDate = makeFormField();
  checkNumber = makeFormField();
  purchaseTotal = makeFormField();
  infoImage: 'receiptDate' | 'receiptCheckNumber' | 'receiptTotal' = 'receiptDate';

  @Prop({ type: Boolean })
  isSubmitting!: boolean;

  get countryCode(): string {
    return getCountryCodeByTopLevelDomain();
  }

  get images() {
    return this.$appStore.state.cms.images.retroCredit[this.countryCode.toLowerCase()];
  }

  get currentInfoImage(): CmsImageUrls {
    return this.images[this.infoImage];
  }

  onDateFocus() {
    this.infoImage = 'receiptDate';
  }

  onCheckNumberFocus() {
    this.infoImage = 'receiptCheckNumber';
  }

  onTotalFocus() {
    this.infoImage = 'receiptTotal';
  }

  onSubmit() {
    if (!this.validate()) {
      return;
    }

    this.$emit('submit', {
      checkNumber: this.checkNumber.value,
      purchaseDate: this.formatDate(this.purchaseDate.value),
      purchaseTotal: this.purchaseTotal.value,
    });
  }

  formatDate(date: string): string {
    const cleanDate = date.replace(/\D/g, '');

    let result = '';
    result += cleanDate.substr(0, 2);

    if (cleanDate.length > 2) {
      result += `/${cleanDate.substr(2, 2)}`;
    }

    if (cleanDate.length > 4) {
      result += `/${cleanDate.substr(4)}`;
    }

    return result;
  }

  validatePurchaseDate(): boolean {
    this.purchaseDate.error = '';

    const value = this.purchaseDate.value;

    if (!value) {
      this.purchaseDate.error = 'Purchase Date Required';
      this.purchaseDate.errorMode = ErrorMode.PlaceHolder;

      return false;
    }

    const cleanValue = value.replace(/\D/g, '');
    const date = moment(this.formatDate(cleanValue));
    if (cleanValue.length != 8 || !date.isValid()) {
      this.purchaseDate.error = 'Invalid Date';
      this.purchaseDate.errorMode = ErrorMode.BelowField;

      return false;
    }

    const today = moment();

    if (date.isBefore(moment().subtract(30, 'days')) || date.isAfter(today)) {
      this.purchaseDate.error = 'Must be within the last 30 days';
      this.purchaseDate.errorMode = ErrorMode.BelowField;

      return false;
    }

    return true;
  }

  validateCheckNumber(): boolean {
    this.checkNumber.error = '';
    this.checkNumber.dirty = true;

    if (!this.checkNumber.value) {
      this.checkNumber.error = 'Check Number Required';
      this.checkNumber.errorMode = ErrorMode.PlaceHolder;

      return false;
    }

    if (this.checkNumber.value.length > 5) {
      this.checkNumber.error = 'Check numbers are only 5 digits';
      this.checkNumber.errorMode = ErrorMode.PlaceHolder;

      return false;
    }

    return true;
  }

  // e.g. $1,480.13
  formatTotal(total: string): string {
    if (!total) {
      return '';
    }

    const cleanTotal = parseFloat(total).toFixed(2);
    this.purchaseTotal.value = cleanTotal;

    // return with $ and commas
    return `$${cleanTotal.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  validatePurchaseTotal(): boolean {
    this.purchaseTotal.error = '';
    this.purchaseTotal.dirty = true;

    if (!this.purchaseTotal.value) {
      this.purchaseTotal.error = 'Purchase Total Required';
      this.purchaseTotal.errorMode = ErrorMode.PlaceHolder;

      return false;
    }

    return true;
  }

  stripNonNumericsKeepPeriod(value: string): string {
    return stripNonNumericsKeepPeriod(value);
  }

  onClickPurchaseDateInfo() {
    this.$services.ui.setModalVisibility('retroCreditReceiptModal', true, {
      imageUrl: this.images.receiptDate.mobile || '',
    });
  }

  onClickCheckNumberInfo() {
    this.$services.ui.setModalVisibility('retroCreditReceiptModal', true, {
      imageUrl: this.images.receiptCheckNumber.mobile || '',
    });
  }

  onClickPurchaseTotalInfo() {
    this.$services.ui.setModalVisibility('retroCreditReceiptModal', true, {
      imageUrl: this.images.receiptTotal.mobile || '',
    });
  }

  private validate() {
    const results = [this.validatePurchaseDate(), this.validateCheckNumber(), this.validatePurchaseTotal()];
    return !results.includes(false);
  }
}
